import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ICategoryDataService,
  Category,
  CategoryTreeNode,
  CreateCategoryDto,
} from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

@Injectable()
export class CategoryDataCloudService implements ICategoryDataService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);

  private get baseUrl(): string {
    return `${this.config.apiBaseUrl}/category`;
  }

  find(): Observable<Category[]> {
    return this.http.get<Category[]>(this.baseUrl);
  }

  findOne(id: string): Observable<Category> {
    return this.http.get<Category>(`${this.baseUrl}/${id}`);
  }

  create(data: CreateCategoryDto): Observable<Category> {
    return this.http.post<Category>(this.baseUrl, data);
  }

  update(id: string, data: Partial<Category>): Observable<Category> {
    return this.http.patch<Category>(`${this.baseUrl}/${id}`, data);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  flat(): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.baseUrl}/flat`);
  }

  flatWithCounts(): Observable<(Category & { count: number })[]> {
    return this.http.get<(Category & { count: number })[]>(
      `${this.baseUrl}/flat/with-counts`
    );
  }

  tree(): Observable<CategoryTreeNode[]> {
    return this.http.get<CategoryTreeNode[]>(`${this.baseUrl}/tree`);
  }

  treeWithCounts(): Observable<CategoryTreeNode[]> {
    return this.http.get<CategoryTreeNode[]>(
      `${this.baseUrl}/tree/with-counts`
    );
  }

  children(parentId: string): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.baseUrl}/children/${parentId}`);
  }

  path(categoryId: string): Observable<Category[]> {
    return this.http.get<Category[]>(`${this.baseUrl}/path/${categoryId}`);
  }

  move(categoryId: string, newParentId: string | null): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/${categoryId}/move`, {
      parent_id: newParentId,
    });
  }
}
